import React from "react";
import PropTypes from "prop-types";
import { Modal, Button, Alert, Badge } from "react-bootstrap";

/**
 * Exibe, em leitura, os aceites que o profissional registrou num programa: as respostas dos termos
 * personalizados, a data e o IP de origem. Usado no cadastro de médicos e de prestadores, porque a
 * aba "Termos" só existe em programas que exigem aceite manual — e o aceite pode vir do pré-cadastro.
 */
class ModalAceitesPrograma extends React.Component {
    formatarResposta = (pergunta, valor) => {
        if (valor === undefined || valor === null || valor === "") {
            return <span className="text-muted">Não respondido</span>;
        }

        if (typeof valor === "boolean") {
            return (
                <Badge bg={valor ? "success" : "danger"}>{valor ? "Aceito" : "Não aceito"}</Badge>
            );
        }

        if (Array.isArray(pergunta?.opcoes)) {
            const opcao = pergunta.opcoes.find((o) => (typeof o === "object" ? o?.valor === valor : o === valor));
            if (opcao) {
                return <span>{typeof opcao === "object" ? opcao?.label || String(opcao?.valor) : String(opcao)}</span>;
            }
        }

        return <span>{String(valor)}</span>;
    };

    formatarDataHora = (data) => {
        if (!data) return null;

        const dataAceite = new Date(data);
        if (Number.isNaN(dataAceite.getTime())) return null;

        return `${dataAceite.toLocaleDateString("pt-BR")} às ${dataAceite.toLocaleTimeString("pt-BR")}`;
    };

    getPerguntas = () => {
        const { vinculo } = this.props;

        // o snapshot gravado no aceite tem precedência: é o texto que a pessoa realmente aceitou,
        // que pode ter mudado na configuração do programa desde então
        const perguntas = vinculo?.termos_dinamicos_perguntas_json;
        if (Array.isArray(perguntas) && perguntas.length) return perguntas;

        const perguntasConfig = this.props.perguntasConfigPrograma;
        return Array.isArray(perguntasConfig) ? perguntasConfig : [];
    };

    render() {
        const { show, onHide, vinculo, receberComunicacoes } = this.props;

        if (!vinculo) return null;

        const respostas = vinculo.termos_dinamicos_respostas_json || {};
        const perguntas = this.getPerguntas();
        const dataAceite = this.formatarDataHora(vinculo.data_aceite_termo);

        return (
            <Modal show={show} size="lg" onHide={onHide}>
                <Modal.Header closeButton>
                    <Modal.Title>Aceites do programa</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h5 className="mb-1">{vinculo.programa_nome}</h5>
                    <div className="text-muted mb-3">
                        {dataAceite ? (
                            <>
                                Aceite registrado em {dataAceite}
                                {vinculo.ip_aceite_termos && <> · IP {vinculo.ip_aceite_termos}</>}
                            </>
                        ) : (
                            "Sem registro de aceite para este programa."
                        )}
                    </div>

                    {/* Comprovante carimbado (quem aceitou, data/hora, IP). So existe em aceites
                        posteriores a essa entrega -- cadastros antigos seguem sem o arquivo. */}
                    {vinculo.termo_pdf_comprovante && (
                        <div className="border rounded p-2 mb-3 bg-light d-flex align-items-center justify-content-between">
                            <div>
                                <div className="fw-semibold mb-1">Comprovante de aceite</div>
                                <div className="text-muted small">
                                    PDF do termo com o registro de quem aceitou, data, hora e IP.
                                </div>
                            </div>
                            <a
                                href={vinculo.termo_pdf_comprovante}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-primary btn-sm ms-2 text-nowrap"
                            >
                                Baixar PDF
                            </a>
                        </div>
                    )}

                    {typeof receberComunicacoes === "boolean" && (
                        <div className="border rounded p-2 mb-3 bg-light">
                            <div className="fw-semibold mb-1">Preferência de comunicação do cadastro</div>
                            <div className="text-muted mb-1">
                                Vale para o cadastro do profissional, não apenas para este programa.
                            </div>
                            <Badge bg={receberComunicacoes ? "success" : "secondary"}>
                                {receberComunicacoes ? "Aceita receber comunicações" : "Não aceita receber comunicações"}
                            </Badge>
                        </div>
                    )}

                    {!perguntas.length ? (
                        <Alert variant="light" className="mb-0">
                            Não há termos personalizados configurados para este programa.
                        </Alert>
                    ) : (
                        perguntas.map((pergunta, index) => {
                            const slug = pergunta?.slug;
                            const label = pergunta?.label || slug || `Pergunta ${index + 1}`;
                            const texto = pergunta?.pergunta || pergunta?.texto || "";

                            return (
                                <div key={slug || `aceite_${index}`} className="border rounded p-2 mb-2 bg-light">
                                    <div className="fw-semibold mb-1">{label}</div>
                                    {!!texto && texto !== label && <div className="text-muted mb-1">{texto}</div>}
                                    <div>{this.formatarResposta(pergunta, slug ? respostas[slug] : undefined)}</div>
                                </div>
                            );
                        })
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

ModalAceitesPrograma.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func.isRequired,
    receberComunicacoes: PropTypes.bool,
    perguntasConfigPrograma: PropTypes.array,
    vinculo: PropTypes.shape({
        programa_nome: PropTypes.string,
        data_aceite_termo: PropTypes.string,
        ip_aceite_termos: PropTypes.string,
        termos_dinamicos_perguntas_json: PropTypes.array,
        termos_dinamicos_respostas_json: PropTypes.object,
    }),
};

export default ModalAceitesPrograma;
